/**
 * Media Metadata Utilities
 * Extract metadata from image and video files for enhanced previews
 */

export interface ImageMetadata {
  width: number;
  height: number;
  aspectRatio: number;
  sizeKB: number;
}

export interface VideoMetadata {
  width: number;
  height: number;
  duration: number; // in seconds
  aspectRatio: number;
  sizeKB: number;
  hasAudio: boolean;
}

/**
 * Get image dimensions and metadata
 * @param file - Image file to analyze
 * @returns Promise with image metadata
 */
export async function getImageMetadata(file: File): Promise<ImageMetadata> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      const aspectRatio = width / height;
      const sizeKB = Math.round(file.size / 1024);

      // Clean up object URL
      URL.revokeObjectURL(url);

      resolve({
        width,
        height,
        aspectRatio,
        sizeKB,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Get video metadata (duration, dimensions, audio)
 * @param file - Video file to analyze
 * @returns Promise with video metadata
 */
export async function getVideoMetadata(file: File): Promise<VideoMetadata> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);

    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      const width = video.videoWidth;
      const height = video.videoHeight;
      const duration = video.duration;
      const aspectRatio = width / height;
      const sizeKB = Math.round(file.size / 1024);

      // Check if video has audio track
      // Note: This is a simplified check; real implementation might need MediaSource API
      const videoWithAudioInfo = video as HTMLVideoElement & {
        mozHasAudio?: boolean;
        webkitAudioDecodedByteCount?: number;
        audioTracks?: { length: number };
      };
      const hasAudio = Boolean(videoWithAudioInfo.mozHasAudio) ||
        Boolean(videoWithAudioInfo.webkitAudioDecodedByteCount) ||
        Boolean(videoWithAudioInfo.audioTracks && videoWithAudioInfo.audioTracks.length > 0);

      // Clean up
      URL.revokeObjectURL(url);

      resolve({
        width,
        height,
        duration,
        aspectRatio,
        sizeKB,
        hasAudio,
      });
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load video metadata'));
    };

    video.src = url;
  });
}

/**
 * Format video duration from seconds to MM:SS or HH:MM:SS
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 */
export function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) {
    return '0:00';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    // HH:MM:SS format
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  } else {
    // MM:SS format
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }
}

/**
 * Format resolution for display (e.g., "1920×1080", "1280×720")
 * @param width - Width in pixels
 * @param height - Height in pixels
 * @returns Formatted resolution string
 */
export function formatResolution(width: number, height: number): string {
  return `${width}×${height}`;
}

/**
 * Get common resolution name (e.g., "1080p", "720p", "4K")
 * @param width - Width in pixels
 * @param height - Height in pixels
 * @returns Common resolution name or null if not standard
 */
export function getResolutionName(width: number, height: number): string | null {
  const maxDimension = Math.max(width, height);

  if (maxDimension >= 3840) return '4K';
  if (maxDimension >= 2560) return '1440p';
  if (maxDimension >= 1920) return '1080p';
  if (maxDimension >= 1280) return '720p';
  if (maxDimension >= 854) return '480p';
  if (maxDimension >= 640) return '360p';

  return null;
}

/**
 * Estimate compressed file size (rough approximation)
 * For images: ~60-70% size reduction with WebP at 75% quality
 * For videos: ~50-60% size reduction with H.264 at CRF 28
 *
 * @param file - File to estimate
 * @param isVideo - Whether the file is a video
 * @returns Estimated compressed size in KB
 */
export function estimateCompressedSize(file: File, isVideo: boolean = false): number {
  const sizeKB = Math.round(file.size / 1024);

  if (isVideo) {
    // Videos typically compress to 40-50% of original size
    return Math.round(sizeKB * 0.45);
  } else {
    // Images typically compress to 30-40% of original size
    return Math.round(sizeKB * 0.35);
  }
}

/**
 * Check if file is compressible media (image or video)
 * @param file - File to check
 * @returns true if file is compressible
 */
export function isCompressibleMedia(file: File): boolean {
  return file.type.startsWith('image/') || file.type.startsWith('video/');
}

/**
 * Format file size with compression savings
 * @param originalSizeKB - Original file size in KB
 * @param compressedSizeKB - Compressed file size in KB
 * @returns Formatted string showing savings
 */
export function formatCompressionSavings(
  originalSizeKB: number,
  compressedSizeKB: number
): string {
  const savingsKB = originalSizeKB - compressedSizeKB;
  const savingsPercent = Math.round((savingsKB / originalSizeKB) * 100);

  if (savingsKB < 1024) {
    return `${savingsKB} KB спестени (${savingsPercent}%)`;
  } else {
    const savingsMB = (savingsKB / 1024).toFixed(1);
    return `${savingsMB} MB спестени (${savingsPercent}%)`;
  }
}
